/* General Material Supplier Pre-Qualification Template - question master.
 *
 * These questions are only rendered when the invitation template name starts
 * with "General" (e.g. "General Supplier Pre-Qualification", "General Material
 * Supplier Qualification", "General Supplier Qualification"). Every other
 * template keeps the existing questions unchanged.
 *
 * Each entry: { name, label, type, options?, mandatory? }
 * types: text | number | year | currency | percent | textarea | table | multi
 *        | select | yesno | yesno_details | upload | auto_years
 */

export const isGeneralTemplate = (template) =>
  /^general\b/i.test(String(template || "").trim());
// eslint-disable-next-line
const YES_NO = ["Yes", "No"];
// eslint-disable-next-line
const YES_NO_NA = ["Yes", "No", "NA"];

/* The General template's own section list - exactly as in the template sheet.
 * When the invitation template is a General one, the questionnaire shows ONLY
 * these tabs (Document Upload / Declaration & Submit app tabs are dropped for
 * the General template). Every other template keeps the existing app tabs. */
export const generalTemplateSections = [
  { key: "companyInformation", label: "General Company Information", icon: "building" },
  { key: "financialInformation", label: "Financial Stability", icon: "cash" },
  { key: "qualityManagement", label: "Product Quality & Quality Management", icon: "award" },
  { key: "commercialInformation", label: "Pricing & Commercial Capability", icon: "cart" },
  { key: "deliveryCapability", label: "Delivery & Logistics Capability", icon: "truck" },
  { key: "technicalCapability", label: "Technical & Operational Capability", icon: "cpu" },
  { key: "complianceLegal", label: "Sustainability, EHS & Ethical Practices", icon: "shield-check" },
  { key: "customerServiceSupport", label: "Customer Service & Support", icon: "headset" },
  { key: "experienceReferences", label: "Experience & Customer References", icon: "briefcase" },
  { key: "riskCompliance", label: "Risk Management & Regulatory Compliance", icon: "exclamation-triangle" },
];

export const getGeneralTemplateSections = () =>
  generalTemplateSections.map((section, index) => ({
    ...section,
    id: index + 1,
  }));

export const generalTemplateQuestions = {
  /* ---- 1. General Company Information ---- */
  companyInformation: [
    { name: "legalCompanyName", label: "Legal Company Name", type: "text", mandatory: true },
    {
      name: "natureOfBusiness",
      label: "Nature of Business",
      type: "select",
      options: ["Manufacturer", "Trader", "Distributor", "Authorized Dealer"],
      mandatory: true,
    },
    { name: "yearsInBusiness", label: "Years in Business", type: "auto_years", mandatory: true },
    
    
    { name: "parentCompany", label: "Parent Company", type: "text" },
    { name: "numberOfEmployees", label: "Number of Employees", type: "number" },
    { name: "msmeStatus", label: "MSME Status?", type: "yesno", mandatory: true },
    { name: "udyamRegistrationNumber", label: "Udyam Registration No. (if applicable)", type: "text" },
    { name: "companyRegistrationCin", label: "Company Registration / CIN (if applicable)", type: "text" },
    { name: "registeredAddress", label: "Registered Address", type: "textarea", mandatory: true },
    { name: "keyManagementPersonnel", label: "Key Management Personnel (Name / Designation / Contact)", type: "table", mandatory: true },
    { name: "companyProfileUpload", label: "Company Profile", type: "upload", mandatory: true },



  ],

  /* ---- 2. Financial Stability ---- */
  financialInformation: [
    { name: "annualTurnoverFy1", label: "Annual Turnover - FY1 (INR)", type: "currency" },
    { name: "annualTurnoverFy2", label: "Annual Turnover - FY2 (INR)", type: "currency" },
    { name: "annualTurnoverFy3", label: "Annual Turnover - FY3 (INR)", type: "currency" },
    { name: "profitLossLast3Years", label: "Profit / Loss - Last 3 Years (INR)", type: "text" },
    { name: "netWorth", label: "Net Worth (INR)", type: "currency" },
    { name: "auditedFinancialStatements", label: "Audited Financial Statements - 3 Years", type: "upload" },
    { name: "creditRatingAvailable", label: "Credit Rating Available?", type: "yesno" },
    { name: "creditRatingDetails", label: "Credit Rating (Rating + Agency)", type: "text" },
    { name: "insolvencyProceedings", label: "Any insolvency / bankruptcy proceedings?", type: "yesno_details" },
    { name: "supportCreditTerms", label: "Able to support required credit terms?", type: "yesno" },
    /* bank / financial block - starts right after the last question above */
    { name: "bankName", label: "Bank Name", type: "text" },
    { name: "bankAccountNumber", label: "Bank Account Number", type: "text" },
    {
      name: "creditRating",
      label: "Credit Rating",
      type: "select",
      options: ["AAA", "AA", "A", "BBB"],
    },
    { name: "ifscCode", label: "IFSC Code", type: "text" },
    { name: "financialRemarks", label: "Financial Remarks", type: "textarea" },
  ],

  /* ---- 3. Product Quality & Quality Management ---- */
  qualityManagement: [
    { name: "iso9001Certified", label: "ISO 9001 Certified? (attach certificate)", type: "yesno", mandatory: true },
    { name: "iso9001Certificate", label: "ISO 9001 Certificate", type: "upload" },
    { name: "otherQualityCertifications", label: "Other Quality Certifications (multi-select)", type: "multi" },
    { name: "qmsAvailable", label: "Quality Management System available?", type: "yesno" },
    { name: "incomingQualityInspection", label: "Incoming Quality Inspection followed?", type: "yesno" },
    { name: "inProcessQualityChecks", label: "In-process Quality Checks followed?", type: "yesno_na" },
    { name: "finalInspectionFollowed", label: "Final Inspection followed?", type: "yesno" },
    { name: "productTestCertificatesCoa", label: "Product Test Certificates / COA available?", type: "yesno" },
    { name: "materialTraceability", label: "Material Traceability available?", type: "yesno" },
    { name: "batchLotTraceability", label: "Batch / Lot Traceability available?", type: "yesno_na" },
    { name: "calibrationProcessMaintained", label: "Calibration Process maintained?", type: "yesno_na" },
    { name: "nonConformanceProcess", label: "Non-Conformance process available?", type: "yesno" },
    { name: "capaProcess", label: "CAPA process available?", type: "yesno" },
    { name: "historicalRejectionRate", label: "Historical Rejection Rate (%)", type: "percent" },
    { name: "customerComplaintProcessQuality", label: "Customer complaint process?", type: "yesno" },
    { name: "samplesAvailableEvaluation", label: "Samples available for evaluation?", type: "yesno" },
    { name: "productCompliantStandards", label: "Product compliant with applicable standards?", type: "yesno_details" },
    { name: "qualityManual", label: "Quality Manual", type: "upload" },
  ],

  /* ---- 4. Pricing & Commercial Capability ---- */
  commercialInformation: [
    { name: "documentedPricingMechanism", label: "Documented / standard pricing mechanism for products offered?", type: "yesno", mandatory: true },
    { name: "volumeBasedDiscounts", label: "Able to offer volume-based / bulk purchase discounts?", type: "yesno", mandatory: true },
    { name: "preferentialPricingLongTerm", label: "Able to offer preferential pricing for long-term contracts?", type: "yesno", mandatory: true },
    { name: "priceValidityPeriod", label: "Standard price validity period (Days / Months)", type: "text", mandatory: true },
    {
      name: "standardPaymentTerms",
      label: "Standard payment terms",
      type: "select",
      options: ["Advance", "Net 30", "Net 45", "Net 60", "Net 90", "Other"],
      mandatory: true,
    },
    { name: "maxCreditPeriodDays", label: "Maximum credit period offered (Days)", type: "number", mandatory: true },
    { name: "maintainAgreedPrices", label: "Able to maintain agreed prices during contract validity?", type: "yesno", mandatory: true },
    {
      name: "priceRevisionMechanism",
      label: "Mechanism followed for price revisions",
      type: "select",
      options: ["Fixed during contract", "Annual revision", "Raw-material index based", "Negotiation based", "Other"],
      mandatory: true,
    },
    { name: "priceRevisionRemarks", label: "Price revision remarks (if any)", type: "text" },
    { name: "costBreakupAvailable", label: "Can you provide a cost breakup when requested?", type: "yesno" },
    {
      name: "freightChargesIncluded",
      label: "Freight charges included in standard pricing?",
      type: "select",
      options: ["Yes", "No", "Depends on Location"],
      mandatory: true,
    },
    { name: "packagingChargesIncluded", label: "Standard packaging charges included in pricing?", type: "yesno", mandatory: true },
    { name: "moqDefined", label: "Defined Minimum Order Quantity (MOQ)? (Qty / UOM)", type: "yesno_details", mandatory: true },
    { name: "warrantyReplacement", label: "Warranty / replacement against defective materials?", type: "yesno_details", mandatory: true },
    { name: "priceNegotiationWilling", label: "Willing to participate in periodic price negotiations / cost-reduction initiatives?", type: "yesno", mandatory: true },
  ],

  /* ---- 5. Delivery & Logistics Capability ---- */
  deliveryCapability: [
    { name: "standardLeadTimeDays", label: "Standard Lead Time (Days)", type: "number" },
    { name: "minimumLeadTimeDays", label: "Minimum Lead Time (Days)", type: "number" },
    { name: "historicalOnTimeDelivery", label: "Historical On-Time Delivery (%)", type: "percent" },
    { name: "historicalOtif", label: "Historical OTIF (%)", type: "percent" },
    { name: "moqDelivery", label: "MOQ (Qty / UOM)", type: "text" },
    { name: "maxMonthlySupplyCapacity", label: "Maximum Monthly Supply Capacity (Qty / UOM)", type: "text" },
    { name: "safetyStockCapability", label: "Safety Stock Capability?", type: "yesno" },
    { name: "safetyStockDays", label: "Safety Stock Days", type: "number" },
    { name: "warehouseAvailable", label: "Warehouse Available?", type: "yesno" },
    { name: "warehouseLocations", label: "Warehouse Locations (Location / Capacity - one per line)", type: "table" },
    {
      name: "logisticsMode",
      label: "Logistics Mode",
      type: "select",
      options: ["Own", "3PL", "Both"],
    },
    {
      name: "deliveryCoverage",
      label: "Delivery Coverage",
      type: "select",
      options: ["Regional", "Pan India", "International"],
    },
    { name: "emergencyDeliveryCapability", label: "Emergency Delivery Capability?", type: "yesno" },
    { name: "emergencyLeadTimeDays", label: "Emergency Lead Time (Days)", type: "number" },
    { name: "supplyChainContingencyPlan", label: "Supply Chain Contingency Plan?", type: "yesno_details" },
  ],

  /* ---- 6. Technical & Operational Capability ---- */
  technicalCapability: [
    { name: "technicalCapabilityAdequate", label: "Adequate technical capability to supply the proposed products / materials?", type: "yesno", mandatory: true },
    { name: "maxMonthlyCapacityProposed", label: "Maximum monthly supply / production capacity for proposed category (Qty + UOM)", type: "text", mandatory: true },
    { name: "capacityUtilizationPercent", label: "Current average capacity utilization (%)", type: "percent", mandatory: true },
    { name: "infrastructureAdequate", label: "Adequate infrastructure / equipment to support the proposed supply?", type: "yesno", mandatory: true },
    { name: "majorMachineryEquipment", label: "Major machinery / equipment relevant to proposed products (one per line)", type: "table" },
    { name: "technicalPersonnel", label: "Dedicated technical / engineering personnel? (number)", type: "yesno_details", mandatory: true },
    {
      name: "inhouseTestingCapability",
      label: "In-house product / material testing capability?",
      type: "select",
      options: ["Yes", "No", "Partially"],
      mandatory: true,
    },
    { name: "criticalProcessesOutsourced", label: "Any critical processes outsourced / subcontracted? (details)", type: "yesno_details", mandatory: true },
    { name: "preventiveMaintenance", label: "Preventive maintenance system for critical machinery / equipment?", type: "yesno_na" },
    { name: "erpSystem", label: "ERP / digital system for production, order or inventory management? (system name)", type: "yesno_details" },
    { name: "barcodeQrTraceability", label: "Barcode / QR-code based identification or traceability supported?", type: "yesno_na" },
    { name: "newProductDevelopment", label: "Capability for new-product / sample development when required?", type: "yesno_na" },
    { name: "technicalChangeProcess", label: "Established process for technical changes / product improvements?", type: "yesno" },
    { name: "productImprovementExample", label: "Example of significant product / process improvement in last 3 years", type: "textarea" },
    { name: "backupEquipment", label: "Backup equipment / capacity for critical production processes? (details)", type: "yesno_details", mandatory: true },
  ],

  /* ---- 7. Sustainability, EHS & Ethical Practices ---- */
  complianceLegal: [
    { name: "environmentalPolicy", label: "Documented Environmental Policy?", type: "yesno_details", mandatory: true },
    { name: "iso14001Certified", label: "ISO 14001 certified? (attach certificate)", type: "yesno" },
    { name: "healthSafetyPolicy", label: "Documented Health & Safety Policy?", type: "yesno_details", mandatory: true },
    { name: "iso45001Certified", label: "ISO 45001 or equivalent certified? (attach certificate)", type: "yesno" },
    { name: "wasteManagementProcess", label: "Defined waste-management / disposal process?", type: "yesno", mandatory: true },
    { name: "environmentalRegulationsCompliance", label: "Comply with applicable environmental and pollution-control regulations?", type: "yesno", mandatory: true },
    { name: "sustainabilityInitiatives", label: "Initiatives to reduce energy, water, waste or emissions? (details)", type: "yesno_details" },
    { name: "workplaceHsIncidentsMonitored", label: "Workplace health and safety incidents monitored?", type: "yesno", mandatory: true },
    { name: "childLabourProhibited", label: "Child labour prohibited? (declaration)", type: "yesno_details", mandatory: true },
    { name: "forcedLabourProhibited", label: "Forced / bonded labour prohibited? (declaration)", type: "yesno_details", mandatory: true },
    { name: "antiBriberyPolicy", label: "Anti-bribery / anti-corruption policy?", type: "yesno", mandatory: true },
    { name: "ethicalSourcingPractices", label: "Ethical and responsible sourcing practices followed?", type: "yesno", mandatory: true },
    { name: "codeOfConduct", label: "Code of Conduct / Business Ethics Policy?", type: "yesno_details" },
    { name: "sustainabilityCsrInitiatives", label: "Sustainability / CSR initiatives in place? (details)", type: "yesno_details" },
    { name: "ehsViolationsLast3Years", label: "Major environmental, health or safety violations in last 3 years? (details)", type: "yesno_details", mandatory: true },
  ],

  /* ---- 8. Customer Service & Support (extra tab) ---- */
  customerServiceSupport: [
    { name: "dedicatedAccountPerson", label: "Dedicated account / contact person assigned for our organization?", type: "yesno", mandatory: true },
    { name: "orderAcknowledgementTime", label: "Standard order acknowledgement turnaround time (Hours / Days)", type: "text", mandatory: true },
    { name: "complaintHandlingProcess", label: "Formal customer complaint-handling process?", type: "yesno", mandatory: true },
    {
      name: "complaintAcknowledgementTime",
      label: "Standard complaint acknowledgement time",
      type: "select",
      options: ["<4 hours", "8 hours", "24 hours", ">24 hours"],
      mandatory: true,
    },
    { name: "complaintResolutionTime", label: "Standard complaint resolution / closure time (Hours / Days)", type: "text", mandatory: true },
    {
      name: "technicalSupportProvided",
      label: "Technical support provided for supplied products where required?",
      type: "select",
      options: ["Yes", "No", "NA"],
      mandatory: true,
    },
    { name: "replacementProvided", label: "Replacement provided for rejected / defective material?", type: "yesno", mandatory: true },
    { name: "replacementTurnaroundDays", label: "Standard replacement turnaround time (Days)", type: "number" },
    { name: "escalationMatrix", label: "Defined escalation matrix for unresolved issues?", type: "yesno_details", mandatory: true },
    { name: "emergencySupport", label: "Emergency support for critical requirements?", type: "yesno", mandatory: true },
    { name: "slaComplianceWilling", label: "Willing to comply with mutually agreed service levels / KPIs?", type: "yesno", mandatory: true },
    { name: "customerFeedbackReview", label: "Customer feedback periodically reviewed and corrective actions implemented?", type: "yesno" },
  ],

  /* ---- 9. Experience & Customer References ---- */
  experienceReferences: [
    { name: "experienceYearsProposedCategory", label: "Years of experience in proposed product / material category", type: "number", mandatory: true },
    { name: "industriesServed", label: "Industries currently served (multi-select, comma separated)", type: "multi", mandatory: true },
    { name: "similarProductsSupplied", label: "Supply products / materials similar to those proposed to us?", type: "yesno", mandatory: true },
    { name: "activeCustomersCount", label: "Active customers served in this category", type: "number", mandatory: true },
    { name: "supplyToOems", label: "Supply to OEMs / large manufacturing organizations?", type: "yesno", mandatory: true },
    { name: "topRelevantCustomers", label: "Top three / five relevant customers (Customer / Industry / Products - one per line)", type: "table", mandatory: true },
    { name: "customerReferencesAvailable", label: "Customer references available for similar supplies?", type: "yesno", mandatory: true },
    { name: "customerReferences", label: "At least two customer references for verification (Name / Company / Phone / Email - one per line)", type: "table", mandatory: true },
    { name: "significantOrdersLast3Years", label: "Significant orders / projects similar to our requirement in last 3 years? (details)", type: "yesno_details", mandatory: true },
    { name: "suppliedToPrestigeTtk", label: "Previously supplied to Prestige TTK or any associated business / entity? (details)", type: "yesno_details", mandatory: true },
    { name: "supplierAwardsRecognitions", label: "Supplier / customer awards or recognitions in last 3 years?", type: "yesno_details" },
    { name: "caseStudiesReferences", label: "Relevant case studies / product performance references?", type: "yesno_details" },
  ],

  /* ---- 10. Risk Management & Regulatory Compliance (extra tab) ---- */
  riskCompliance: [
    { name: "businessContinuityPlan", label: "Documented Business Continuity / Disaster Recovery Plan?", type: "yesno_details", mandatory: true },
    { name: "alternateSourcesCriticalMaterials", label: "Alternate sources for critical raw materials / components? (details)", type: "yesno_details", mandatory: true },
    { name: "alternateSupplyLocation", label: "Alternate manufacturing / supply location in case of disruption? (details)", type: "yesno_details", mandatory: true },
    { name: "majorSupplyDisruptionLast3Years", label: "Major supply disruption in last 3 years? (details)", type: "yesno_details", mandatory: true },
    { name: "supplyChainRiskAssessment", label: "Supply-chain risks periodically identified and assessed?", type: "yesno", mandatory: true },
    { name: "insuranceCoverageAdequate", label: "Adequate insurance coverage maintained for business operations?", type: "yesno", mandatory: true },
    { name: "insurancePolicies", label: "Insurance policies currently maintained (multi-select + details)", type: "multi", mandatory: true },
    { name: "productLiabilityInsurance", label: "Product liability insurance maintained where applicable?", type: "yesno_na" },
    { name: "statutoryRegistrationsValid", label: "All statutory registrations / licenses required for operations currently valid?", type: "yesno", mandatory: true },
    { name: "regulatoryNonComplianceLast3Years", label: "Material regulatory non-compliance in last 3 years? (details)", type: "yesno_details", mandatory: true },
    { name: "legalLitigationPending", label: "Material legal / litigation proceedings pending against your organization? (details)", type: "yesno_details", mandatory: true },
    { name: "blacklistedDebarred", label: "Ever blacklisted / debarred by a customer or government authority? (details)", type: "yesno_details", mandatory: true },
    { name: "conflictsOfInterest", label: "Potential conflicts of interest with our employees / representatives? (details)", type: "yesno_details", mandatory: true },
    { name: "labourEnvTaxCompliance", label: "Comply with applicable labour, environmental, tax and business regulations?", type: "yesno", mandatory: true },
    { name: "complianceMonitoringProcess", label: "Internal process to monitor regulatory / statutory compliance?", type: "yesno", mandatory: true },
    { name: "notifySignificantChanges", label: "Willing to notify us of significant regulatory, ownership, financial or operational changes?", type: "yesno", mandatory: true },
  ],
};

export const getGeneralQuestions = (sectionKey) =>
  generalTemplateQuestions[sectionKey] || [];

/* Find which section a question name belongs to (used by the form to route
 * ComboboxField selections into the right state slice). Handles both the main
 * field and the "...Details" companion field of yesno_details questions. */
export const findGeneralQuestionSection = (name) => {
  if (!name) return null;
  const keys = Object.keys(generalTemplateQuestions);
  for (const key of keys) {
    const hit = generalTemplateQuestions[key].some(
      (q) => q.name === name || `${q.name}Details` === name
    );
    if (hit) return key;
  }
  return null;
};

/* Questions with mandatory:true in the template are marked above, but the UI
 * enforces only this core subset ("anni mandatory vadhu, konni mathrame").
 * Everything else keeps its asterisk off and never blocks Next / Submit. */
const coreMandatoryNames = new Set([
  /* companyInformation */
  "legalCompanyName",
  "registeredAddress",
  "yearEstablished",
  "gstin",
  "pan",
  /* qualityManagement */
  "iso9001Certified",
  /* commercialInformation */
  "standardPaymentTerms",
  "maxCreditPeriodDays",
  /* technicalCapability */
  "technicalCapabilityAdequate",
  "maxMonthlyCapacityProposed",
  /* complianceLegal */
  "environmentalRegulationsCompliance",
  "antiBriberyPolicy",
  /* customerServiceSupport */
  "dedicatedAccountPerson",
  "complaintHandlingProcess",
  /* experienceReferences */
  "experienceYearsProposedCategory",
  "customerReferencesAvailable",
  /* riskCompliance */
  "statutoryRegistrationsValid",
  "labourEnvTaxCompliance",
]);

export const isGeneralMandatory = (q) =>
  q?.mandatory === true && coreMandatoryNames.has(q.name);

/* Mandatory questions that must have an answer before moving on / submitting.
 * Upload-type questions have an inline uploader in the General form, so a
 * mandatory upload is satisfied once its attachmentId is set. */
export const getGeneralMandatoryQuestions = (sectionKey) =>
  getGeneralQuestions(sectionKey).filter((q) => isGeneralMandatory(q));
